import React, { useEffect } from "react"
import { Button, Img, Flex, Box } from "@chakra-ui/react"
import { type RefObject } from "react"
import { chevronSImg, closeIconImg } from "../images"
import { type MenuBoxProps } from "./menubox"
import { type MenuItemInterface, type ButtonRefType, type MenuBoxMasterDataType } from "./menu"
import { MenuFMPButton } from "./menuFMPButton"
import { NewAuthHeader } from "../auth/v2/NewAuthHeader"
import { ProductFilterHeaderButton } from "./productFilterHeaderButton"
import { isNewLoginUIEnabled } from "./menu.support"
import { AnimatePresence, motion } from "framer-motion"

const PANEL_WIDTH = 280

interface MobileOnlyDropdownProps {
  setMobileMenu: {
    on: () => void
    off: () => void
  }
  menuMasterData: MenuBoxMasterDataType
  menuRefs: ButtonRefType
  mobileContainerRef: RefObject<HTMLDivElement>
  handleMouseEnter: (index: number, subMenu: MenuBoxProps[] | null, mainMenu: MenuItemInterface, onclickLink?: string) => void
}

export const MobileOnlyMainMenuDropdown = ({
  setMobileMenu,
  menuMasterData,
  menuRefs,
  mobileContainerRef,
  handleMouseEnter
}: MobileOnlyDropdownProps): JSX.Element => {
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenu.off()
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [setMobileMenu])

  return (
    <AnimatePresence>
      {/* Overlay */}
      <Box
        key="mobile-menu-overlay"
        w="100vw"
        h="100vh"
        pos="fixed"
        top="0"
        left="0"
        bg="rgba(0,0,0,0.5)"
        zIndex={100}
        onClick={setMobileMenu.off}
      />

      {/* Panel */}
      <Box
        key="mobile-menu-panel"
        pos="fixed"
        top="0"
        left="0"
        width={`${PANEL_WIDTH}px`}
        h="100vh"
        zIndex={102}
        overflowY="auto"
      >
        <motion.div
          initial={{ x: -PANEL_WIDTH }}
          animate={{ x: 0, transition: { duration: 0.25, ease: "easeOut" } }}
          exit={{ x: -PANEL_WIDTH, transition: { duration: 0.25, ease: "easeIn" } }}
          style={{ height: "100%", backgroundColor: "var(--chakra-colors-primary)" }}
        >
          <Box p="4" pr="0" ref={mobileContainerRef}>
            <Button
              onClick={setMobileMenu.off}
              w="30px"
              aria-label="Close menu"
              bg="transparent"
              p="0"
              pt="env(safe-area-inset-top)"
              color="white"
              borderRadius={0}
              alignItems="center"
              display="flex"
              _hover={{ opacity: 0.7 }}
              _focus={{
                outline: "2px solid",
                outlineColor: "white",
                outlineOffset: "2px"
              }}
            >
              <Img w="30px" src={closeIconImg} alt="Close menu" />
            </Button>
          </Box>

          <Flex flexDirection="column" align="flex-start" pt="24px" px="4">
            {menuMasterData.map(({ mainMenu, onclickLink, subMenu, id }, index) => (
              <Flex key={`${id}-${index}`} align="center" w="100%" alignItems="stretch" mb="4">
                <Button
                  w="100%"
                  textAlign="left"
                  justifyContent="space-between"
                  alignItems="center"
                  p="0"
                  m="0"
                  my="3px"
                  h="auto"
                  borderRadius={0}
                  name={mainMenu}
                  variant="RButtonBold"
                  whiteSpace="normal"
                  textTransform="uppercase"
                  ref={menuRefs[index]}
                  _hover={{ opacity: 0.7 }}
                  _focus={{
                    outline: "2px solid",
                    outlineColor: "white",
                    outlineOffset: "2px"
                  }}
                  pr="4"
                  onClick={() => {
                    handleMouseEnter(index, subMenu, { name: mainMenu, link: onclickLink }, onclickLink)
                  }}
                >
                  {mainMenu}
                </Button>
                {subMenu && (
                  <Button
                    variant="transparent"
                    m="0"
                    p="0"
                    ml="2"
                    h="auto"
                    w="30px"
                    onClick={() => {
                      handleMouseEnter(index, subMenu, { name: mainMenu, link: onclickLink })
                    }}
                  >
                    <Img src={chevronSImg} h="22px" sx={{ transform: "rotate(-90deg)" }} alt="Expand submenu" />
                  </Button>
                )}
              </Flex>
            ))}

            <Box mt="8px" mb="12px" ml="22px">
              <ProductFilterHeaderButton onNavigate={setMobileMenu.off} />
            </Box>

            <Box w="100%">
              {isNewLoginUIEnabled() ? (
                <NewAuthHeader onNavigate={setMobileMenu.off} />
              ) : (
                <MenuFMPButton onNavigate={setMobileMenu.off} />
              )}
            </Box>
          </Flex>
        </motion.div>
      </Box>
    </AnimatePresence>
  )
}
